import { makeRequest, HttpMethods } from '../helpers/http.helper';
import { isDemoTraffic } from '../demo/core';

// OneSignal App ID from environment variables
const getOneSignalAppId = (): string => {
  const mode = import.meta.env.VITE_MODE;
  if (mode === 'prod') {
    return import.meta.env.VITE_PROD_ONE_SIGNAL_APP_ID || '';
  } else if (mode === 'dev') {
    return import.meta.env.VITE_DEV_ONE_SIGNAL_APP_ID || '';
  } else {
    // TESTING APP ID
    return '1175383d-5cad-4bfa-9d77-3fdc30d55e20'; 
  }
};

declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: any) => void>;
    OneSignal?: any;
  }
}

class OneSignalService {
  private isInitialized = false;
  private OneSignal: any = null;
  private customClickHandler: ((notification: any) => void) | null = null;

  // Initialize OneSignal
  async initialize(): Promise<void> {
    // No push in the public demo: never load or talk to OneSignal.
    if (isDemoTraffic()) {
      return;
    }
    if (this.isInitialized) {
      console.log('OneSignal: Already initialized');
      return;
    }

    const appId = getOneSignalAppId();
    if (!appId) {
      console.warn('OneSignal: App ID not configured');
      return;
    }

    console.log('OneSignal: Initializing with App ID:', appId);

    try {
      // Wait for OneSignal to be available
      if (typeof window === 'undefined' || !(window as any).OneSignal) {
        console.warn('OneSignal: SDK not loaded yet, waiting...');
        await this.waitForOneSignal();
      }

      this.OneSignal = (window as any).OneSignal;

      // Check if OneSignal is already initialized
      if (this.OneSignal && this.OneSignal.SessionManager) {
        try {
          const currentAppId = await this.OneSignal.getAppId?.();
          if (currentAppId) {
            console.log('OneSignal: Already initialized with App ID:', currentAppId);
            if (currentAppId === appId) {
              this.setupNotificationHandlers();
              this.isInitialized = true;
              console.log('OneSignal: Reusing existing initialization');
              return;
            } else {
              console.warn('OneSignal: Already initialized with different App ID:', currentAppId);
              return;
            }
          }
        } catch (e) {
          console.log('OneSignal: Checking initialization state...');
        }
      }

      // Initialize OneSignal
      console.log('OneSignal: Starting init with App ID:', appId);
      
      await this.OneSignal.init({
        appId: appId,
      });

      console.log('OneSignal: Init completed, setting up handlers...');

      // Setup notification handlers
      this.setupNotificationHandlers();

      this.isInitialized = true;
      console.log('OneSignal: Successfully initialized and ready!');
    } catch (error: any) {
      // Check if error is "already initialized" - this is okay
      if (error?.message?.includes('already initialized') || error?.message?.includes('SDK already initialized')) {
        console.log('OneSignal: SDK was already initialized, setting up handlers...');
        this.setupNotificationHandlers();
        this.isInitialized = true;
        return;
      }
      
      // Check if error is "AppID doesn't match" - OneSignal was initialized with different App ID
      if (error?.message?.includes("AppID doesn't match") || error?.message?.includes("doesn't match existing apps")) {
        console.warn('OneSignal: App ID mismatch. OneSignal was previously initialized with a different App ID.');
        this.isInitialized = true; // Mark as initialized to prevent retries
        return;
      }
      
      console.error('OneSignal: Initialization failed:', error);
      console.warn('OneSignal: Continuing without push notifications');
      this.isInitialized = true; // Mark as initialized to prevent retries
    }
  }

  // Wait for OneSignal SDK to load
  private waitForOneSignal(): Promise<void> {
    return new Promise((resolve) => {
      if ((window as any).OneSignal) {
        resolve();
        return;
      }

      if ((window as any).OneSignalDeferred) {
        (window as any).OneSignalDeferred.push((OneSignal: any) => {
          (window as any).OneSignal = OneSignal;
          resolve();
        });
        return;
      }

      const checkInterval = setInterval(() => {
        if ((window as any).OneSignal) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        console.warn('OneSignal: Timeout waiting for SDK to load');
        resolve();
      }, 15000);
    });
  }

  // Check if OneSignal is ready
  isReady(): boolean {
    if (isDemoTraffic()) {
      return false;
    }
    return this.isInitialized && this.OneSignal !== null;
  }

  // Setup notification event handlers
  private setupNotificationHandlers() {
    try {
      if (!this.OneSignal) return;

      this.OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
        console.log('🔔 OneSignal: Notification received in foreground:', event);
      });

      this.OneSignal.Notifications.addEventListener('click', (event: any) => {
        console.log('🔔 OneSignal: Notification clicked:', event);
        
        const notification = event.notification;
        const additionalData = notification.additionalData;

        if (this.customClickHandler) {
          this.customClickHandler(notification);
        }

        this.handleNotificationClick(additionalData);
      });

      this.OneSignal.User.PushSubscription.addEventListener('change', (event: any) => {
        console.log('🔔 OneSignal: Push subscription changed:', event);
      });

      console.log('OneSignal: Notification handlers setup complete');
    } catch (error) {
      console.error('OneSignal: Failed to setup handlers:', error);
    }
  }

  // Get subscription ID (player ID)
  async getPlayerId(): Promise<string | null> {
    if (isDemoTraffic()) {
      return null;
    }
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return null;
      }

      const subscriptionId = this.OneSignal.User.PushSubscription.id;
      
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (subscriptionId && uuidRegex.test(subscriptionId)) {
        console.log('OneSignal: Valid Subscription ID:', subscriptionId);
        return subscriptionId;
      }

      console.log('OneSignal: Subscription is pending, waiting for change event...');
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('OneSignal: Timeout waiting for valid subscription ID');
          resolve(null);
        }, 10000);

        const unsubscribe = this.OneSignal.User.PushSubscription.addEventListener('change', (change: any) => {
          const newId = change.current.id;
          
          if (newId && newId !== 'pending' && uuidRegex.test(newId)) {
            console.log('OneSignal: Valid Subscription ID received:', newId);
            clearTimeout(timeout);
            unsubscribe();
            resolve(newId);
          }
        });
      });
    } catch (error) {
      console.error('OneSignal: Failed to get subscription ID:', error);
      return null;
    }
  }

  // Register player with backend API
  async registerPlayerWithBackend(organizationId: string, userId?: string): Promise<boolean> {
    if (isDemoTraffic()) {
      return false;
    }
    try {
      console.log('OneSignal: Registering player with backend...');

      const playerId = await this.getPlayerId();
      if (!playerId) {
        console.warn('OneSignal: No player ID available for registration');
        return false;
      }

      await makeRequest({
        url: '/notification/register-player',
        method: HttpMethods.POST,
        data: {
          organization_id: organizationId,
          player_id: playerId,
          platform: 'web',
          external_user_id: userId || undefined
        }
      });

      console.log('OneSignal: Player registered with backend successfully');
      return true;
    } catch (error) {
      console.error('OneSignal: Failed to register player with backend:', error);
      return false;
    }
  }

  // Unregister player from backend API
  async unregisterPlayerFromBackend(): Promise<boolean> {
    if (isDemoTraffic()) {
      return false;
    }
    try {
      console.log('OneSignal: Unregistering player from backend...');

      const playerId = await this.getPlayerId();
      if (!playerId) {
        console.warn('OneSignal: No player ID available for unregistration');
        return false;
      }

      await makeRequest({
        url: `/notification/unregister-player/${playerId}`,
        method: HttpMethods.POST,
        data: {}
      });

      console.log('OneSignal: Player unregistered from backend successfully');
      return true;
    } catch (error) {
      console.error('OneSignal: Failed to unregister player from backend:', error);
      return false;
    }
  }

  // Set external user ID
  async setExternalUserId(userId: string) {
    if (isDemoTraffic()) {
      return;
    }
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return;
      }

      await this.OneSignal.login(userId);
      console.log('OneSignal: External user ID set:', userId);
    } catch (error) {
      console.error('OneSignal: Failed to set external user ID:', error);
    }
  }

  // Handle notification click actions
  private handleNotificationClick(additionalData: any) {
    if (!additionalData) {
      return;
    }

    console.log('OneSignal: Handling notification click:', additionalData);

    switch (additionalData.type) {
      case 'new_message':
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/chat?org=${additionalData.organization_id}`;
        }
        break;

      case 'new_invoice':
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/invoices?org=${additionalData.organization_id}`;
        }
        break;

      case 'new_report':
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/reports?org=${additionalData.organization_id}`;
        }
        break;

      default:
        console.log('Unknown notification type:', additionalData.type);
    }
  }

  // Set a custom notification click handler
  setCustomClickHandler(handler: (notification: any) => void) {
    this.customClickHandler = handler;
    console.log('OneSignal: Custom click handler set');
  }
}

export default new OneSignalService();

